import React from 'react';
import { OrderStatus } from '../../lib/types';

// ═══ CONSTANTS ═══
export const ALL_STATUSES: OrderStatus[] = ['Pending', 'Ordered', 'Delivered', 'Closed'];
// Chart palette is hex (not tokens) because SVG `stroke` / `fill` need a real
// colour string — older WebKit doesn't always resolve `var()` inside SVG
// presentation attributes. Charts that need theme-aware colours use the
// `style={{ stroke: 'var(--…)' }}` pattern (D69, WS9).
export const CHART_COLORS = [
  '#16A34A', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444',
  '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
];

// ═══ STYLES — thin adapter over design tokens ═══
//
// All colours pull from CSS custom properties declared in design-tokens.css
// so the mobile layout flips cleanly between light/dark modes and stays
// visually consistent with the desktop UI.
//
// Touch targets: every interactive element hits the 44px iOS minimum.

export const MIN_TOUCH = 44;

export const S = {
  safe: { paddingTop: 'env(safe-area-inset-top, 20px)' } as React.CSSProperties,
  header: {
    background: 'var(--surface)',
    display: 'flex',
    alignItems: 'center',
    padding: '0 var(--sp-3)',
    height: 58,
    borderBottom: '0.5px solid var(--border)',
    position: 'sticky' as const,
    top: 0,
    zIndex: 100,
  },
  card: {
    background: 'var(--surface)',
    borderRadius: 'var(--r-lg)',
    overflow: 'hidden' as const,
  },
  pill: (bg: string, color: string) => ({
    background: bg,
    color,
    padding: '2px var(--sp-2)',
    borderRadius: 'var(--r-full)',
    fontSize: 'var(--fs-xs)',
    fontWeight: 500 as const,
    whiteSpace: 'nowrap' as const,
  }),
  bottomNav: {
    position: 'fixed' as const,
    bottom: 0, left: 0, right: 0,
    height: 56,
    background: 'var(--surface)',
    borderTop: '0.5px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingBottom: 'env(safe-area-inset-bottom, 8px)',
    zIndex: 200,
  },
  sheet: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 300,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'flex-end',
  },
  sheetContent: {
    background: 'var(--surface)',
    borderRadius: '20px 20px 0 0',
    maxHeight: '85vh',
    overflow: 'auto' as const,
    paddingBottom: 'env(safe-area-inset-bottom, 16px)',
  },
  handle: {
    width: 36,
    height: 4,
    background: 'var(--text3)',
    borderRadius: 2,
    margin: '12px auto 4px',
  },
  actionBtn: (bg: string, color: string) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 14px',
    minHeight: MIN_TOUCH,
    background: bg,
    borderRadius: 'var(--r-lg)',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 'var(--fs-base)',
    fontWeight: 500 as const,
    color,
    transition: 'transform var(--dur-fast) var(--ease)',
  }),
  input: {
    // No inline fontSize — let the @media (max-width: 768px) rule in app.css
    // set 16px on mobile so iOS Safari doesn't auto-zoom on focus.
    width: '100%',
    padding: '10px 12px',
    minHeight: MIN_TOUCH,
    borderRadius: 'var(--r-md)',
    border: '1px solid var(--border2)',
    fontFamily: 'inherit',
    outline: 'none',
    boxSizing: 'border-box' as const,
    color: 'var(--text)',
    background: 'var(--surface2)',
  },
  label: {
    fontSize: 'var(--fs-sm)',
    fontWeight: 600 as const,
    color: 'var(--text2)',
    marginBottom: 4,
    display: 'block' as const,
  },
  greenBtn: {
    width: '100%',
    padding: 14,
    minHeight: MIN_TOUCH,
    borderRadius: 'var(--r-lg)',
    border: 'none',
    background: 'var(--accent)',
    color: '#fff',
    fontSize: 'var(--fs-lg)',
    fontWeight: 600 as const,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  fullSheet: {
    position: 'fixed' as const,
    inset: 0,
    zIndex: 350,
    background: 'var(--bg)',
    display: 'flex',
    flexDirection: 'column' as const,
    overflow: 'hidden' as const,
  },
};

// ═══ ANIMATION CSS ═══
// All keyframes align with the 350ms "med" duration token.
export const MOBILE_CSS = `
  @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes fadeSlide { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes backdropIn { from { background: rgba(0,0,0,0); } to { background: rgba(0,0,0,0.4); } }
  @keyframes slideDown { from { transform: translateY(0); } to { transform: translateY(100%); } }
  @keyframes backdropOut { from { background: rgba(0,0,0,0.4); } to { background: rgba(0,0,0,0); } }
  .m-sheet-backdrop { animation: backdropIn 0.35s ease forwards; }
  .m-sheet-content  { animation: slideUp 0.45s cubic-bezier(0.32, 0.72, 0, 1) forwards; }
  .m-sheet-closing .m-sheet-content { animation: slideDown 0.35s cubic-bezier(0.32, 0.72, 0, 1) forwards; }
  .m-sheet-closing  { animation: backdropOut 0.35s ease forwards; }
  .m-tab-content    { animation: fadeSlide 0.4s ease-out forwards; }
  .m-nav-btn        { transition: transform 0.2s ease; -webkit-tap-highlight-color: transparent; min-height: 44px; }
  .m-nav-btn:active { transform: scale(0.9); }
  /* Active-tab pill — subtle accent tint behind icon+label, contained within the
     button. Background, icon stroke, and label colour all transition together
     so switching tabs reads as a smooth crossfade rather than an instant jump. */
  .m-nav-pill {
    display: inline-flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 2px; padding: 4px 12px; border-radius: 12px;
    background: transparent;
    transition: background 0.2s var(--ease);
  }
  .m-nav-pill.is-active { background: var(--accent-bg); }
  .m-nav-pill svg, .m-nav-pill span { transition: color 0.2s var(--ease), stroke 0.2s var(--ease); }
  .m-row            { transition: background 0.15s ease; -webkit-tap-highlight-color: transparent; min-height: 44px; }
  .m-row:active     { background: var(--surface2) !important; }
  /* Mobile reuses desktop's .sc / .sl / .sn / .cr|cb|cg|cn cards with a tighter
     padding so the 2-col grid doesn't blow up on small screens. The desktop
     padding (14px 16px) reads as too sprawling at 50%-viewport card widths. */
  .m-sc.sc          { padding: 10px 12px; border-radius: 10px; cursor: pointer;
                      transition: border-color 0.2s ease, transform 0.15s ease; -webkit-tap-highlight-color: transparent; }
  .m-sc.sc:active   { transform: scale(0.97); }
  .m-sc .sn         { font-size: 22px; margin-top: 0; }
  .m-sc .sl         { font-size: 11px; color: var(--text2); }
  .m-full-sheet     { animation: slideUp 0.4s cubic-bezier(0.32, 0.72, 0, 1) forwards; }

  /* Tablet: desktop layout with tighter proportions */
  @media (min-width: 768px) and (max-width: 1023px) {
    .tc { padding: 0 var(--sp-5) var(--sp-4); }
    header { padding: 0 var(--sp-4); }
    .stats-bar { grid-template-columns: repeat(4, 1fr) !important; }
    /* iPad analytics: let parent handle scroll, avoid nested overflow */
    .pane.active { -webkit-overflow-scrolling: touch; }
  }
`;

// ═══ SHARED HELPERS ═══
export const roleLabel = (r: string) =>
  r === 'super_admin' ? 'Super Admin' :
  r === 'org_admin'   ? 'Account Owner' :
  r === 'dept_admin'  ? 'Team Member' : r;

// Use design-system tokens rather than hard-coded light-mode hex so the
// status pills respect dark mode without needing overrides.
export const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  Pending:   { bg: 'var(--red-bg)',     text: 'var(--red)'   },
  Ordered:   { bg: 'var(--blue-bg)',    text: 'var(--blue)'  },
  Delivered: { bg: 'var(--teal-bg)',    text: 'var(--teal)'  },
  Closed:    { bg: 'var(--surface2)',   text: 'var(--text2)' },
};

// Maps mobile's status string to the .pill-u tone modifier in components.css.
// `green` resolves to teal-bg + teal text (the same recipe mobile already uses
// for Delivered), so this is a class-adoption move, not a colour change.
export const STATUS_TONE: Record<string, 'red' | 'blue' | 'green' | 'gray'> = {
  Pending:   'red',
  Ordered:   'blue',
  Delivered: 'green',
  Closed:    'gray',
};

// .pill-u tone modifier + display label for inventory_log actions. Replaces
// the previous ACTION_COLORS lookup which carried inline `bg` / `text` token
// strings — those are now redundant once consumers adopt the `.pill-u <tone>`
// class.
export const ACTION_TONE: Record<string, 'green' | 'blue' | 'purple' | 'red'> = {
  ADD:    'green',
  ISSUE:  'blue',
  RETURN: 'purple',
  DELETE: 'red',
};

export const ACTION_LABEL: Record<string, string> = {
  ADD: 'Received', ISSUE: 'Issued', RETURN: 'Returned', DELETE: 'Deleted',
};

export function closeSheetAnim(setClosing: (v: string | null) => void, setter: (v: any) => void, name: string) {
  setClosing(name);
  setTimeout(() => { setter(false); setter(null); setClosing(null); }, 350);
}
